import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SignalBadge from "@/components/SignalBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatRoas } from "@/lib/signal";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Lock, Sparkles } from "lucide-react";

export default function History() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/logs").then((r) => setLogs(r.data || [])).finally(() => setLoading(false));
  }, []);

  const total = logs.length;
  const avg = total ? logs.reduce((a, b) => a + b.roas, 0) / total : 0;
  // Full chart is unlocked for anyone with access (trial or Pro), plus the
  // first 2 free-tier entries so brand-new users get a taste.
  const showFullChart = Boolean(user?.has_access) || total <= 2;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="history-page">
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">History</h1>
        <p className="mt-2 text-slate-600">Every week you've logged.</p>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Summary label="Weeks logged" value={total} />
          <Summary label="Avg ROAS" value={formatRoas(avg)} />
          <Summary
            label="Latest signal"
            value={logs[logs.length - 1]?.signal?.toUpperCase() || "—"}
            badge={<SignalBadge signal={logs[logs.length - 1]?.signal} />}
          />
          <Summary label="Last entry" value={logs[logs.length - 1]?.week_label || "—"} />
        </div>

        {/* Trend chart */}
        <Card className="mt-8 p-6 rounded-2xl bg-white border-slate-200 relative overflow-hidden">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-display font-semibold text-lg text-slate-900">ROAS trend</h3>
            {!showFullChart && (
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-slate-500">
                <Lock className="h-3 w-3" /> Full chart locked
              </span>
            )}
          </div>
          <div className="h-64 relative">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(showFullChart ? logs : logs.slice(0, 2)).map((l, i) => ({ idx: i + 1, roas: l.roas, be: l.break_even_roas }))} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="idx" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="be" stroke="#94a3b8" strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="roas" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: "#0f172a" }} />
              </LineChart>
            </ResponsiveContainer>
            {!showFullChart && (
              <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-white grid place-items-end pb-6">
                <Link to="/upgrade">
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="history-unlock-cta">
                    <Sparkles className="mr-2 h-4 w-4" /> Unlock full chart with Pro
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card>

        {/* List */}
        <Card className="mt-8 rounded-2xl bg-white border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-400">
                <th className="px-5 py-3">Week</th>
                <th className="px-5 py-3">Spend</th>
                <th className="px-5 py-3">Revenue</th>
                <th className="px-5 py-3">ROAS</th>
                <th className="px-5 py-3">Buffer</th>
                <th className="px-5 py-3">Signal</th>
                <th className="px-5 py-3">Adherence</th>
              </tr>
            </thead>
            <tbody data-testid="history-table-body">
              {loading ? (
                <tr><td colSpan="7" className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="7" className="px-5 py-8 text-center text-slate-400">No entries yet.</td></tr>
              ) : (
                [...logs].reverse().map((l) => (
                  <tr key={l.log_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{l.week_label}</td>
                    <td className="px-5 py-3 text-slate-700">${l.ad_spend.toLocaleString()}</td>
                    <td className="px-5 py-3 text-slate-700">${l.revenue.toLocaleString()}</td>
                    <td className="px-5 py-3 font-semibold">{formatRoas(l.roas)}</td>
                    <td className={`px-5 py-3 font-semibold ${l.buffer >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {(l.buffer >= 0 ? "+" : "") + formatRoas(l.buffer)}
                    </td>
                    <td className="px-5 py-3"><SignalBadge signal={l.signal} /></td>
                    <td className="px-5 py-3"><AdherencePill status={l.adherence_status} delta={l.next_week_delta_pct} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      </main>
    </div>
  );
}

const AdherencePill = ({ status, delta }) => {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const styles = {
    followed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    partial: "bg-amber-100 text-amber-700 border-amber-200",
    ignored: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = { followed: "Followed", partial: "Partial", ignored: "Ignored" }[status] || status;
  const deltaLabel = typeof delta === "number" ? ` · ${delta > 0 ? "+" : ""}${delta}%` : "";
  return (
    <span
      data-testid={`adherence-pill-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${styles[status] || styles.ignored}`}
    >
      {label}{deltaLabel}
    </span>
  );
};

const Summary = ({ label, value, badge }) => (
  <Card className="lift p-5 rounded-2xl bg-white border-slate-200">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <div className="mt-2 flex items-center gap-2">
      <p className="font-display font-bold text-2xl text-slate-900">{value}</p>
      {badge}
    </div>
  </Card>
);
